/**
 * Angaben für den Aufbauteil des Excel-Kurses, die die Momentaufnahme aus
 * `workbook.ts` nicht mitführt: blattlokale (auch versteckte) Namen,
 * Power-Query-Abfragen, Diagrammdetails, Notizen, Formen, Rahmenlinien,
 * Hyperlinks, Kopf- und Fußzeilen, Seitenumbrüche, Berechnungsmodus und die
 * Fensteransicht.
 *
 * Die Anforderungssätze reichen von ExcelApi 1.1 (Berechnungsmodus) bis 1.18
 * (Notizen), die Fensteransicht gibt es nur in ExcelApiDesktop 1.1. Jede
 * Funktion liefert `null`, wenn ihr Satz fehlt – die Aufgabe entscheidet dann,
 * ob sie auf ein Prüfmakro ausweicht oder offen sagt, dass sie nicht prüfen
 * kann.
 */

import { excelApi, supportsExcelApi } from "./capabilities";
import { ExcelUnavailableError, isExcelAvailable, splitAddress } from "./workbook";
import { isSetSupported } from "@/office/requirements";

function sicher<T>(read: () => T): T | null {
  try {
    const result = read();
    return result === undefined ? null : result;
  } catch {
    return null;
  }
}

function pruefeExcel(): void {
  if (!isExcelAvailable()) throw new ExcelUnavailableError();
}

/* ---------- Blattlokale Namen ---------- */

export interface BlattName {
  sheetName: string;
  name: string;
  visible: boolean | null;
  /** Formel des Namens, z. B. "=Tabelle1!$B$4". `null` vor 1.7. */
  formula: string | null;
}

/**
 * Namen, die auf ein Blatt begrenzt sind – auch die versteckten, die Excel
 * selbst anlegt: `_FilterDatenbank`/`_FilterDatabase` und
 * `Kriterien`/`Criteria` beim Spezialfilter, `solver_opt`, `solver_adj` … für
 * ein Solver-Modell. `null` vor ExcelApi 1.4.
 */
export async function readBlattNamen(): Promise<BlattName[] | null> {
  pruefeExcel();
  if (!supportsExcelApi(excelApi.namensbereich)) return null;
  const mitFormel = supportsExcelApi(excelApi.ansicht);

  try {
    return await Excel.run(async (context) => {
      const sheets = context.workbook.worksheets;
      sheets.load("items/name");
      await context.sync();
      const sammlungen = sheets.items.map((sheet) => {
        const names = sheet.names;
        names.load(mitFormel ? "items/name,items/visible,items/formula" : "items/name,items/visible");
        return { sheetName: sheet.name, names };
      });
      await context.sync();
      return sammlungen.flatMap(({ sheetName, names }) =>
        names.items.map((item) => ({
          sheetName,
          name: String(sicher(() => item.name) ?? ""),
          visible: sicher(() => item.visible),
          formula: mitFormel ? (typeof sicher(() => item.formula) === "string" ? String(item.formula) : null) : null,
        })),
      );
    });
  } catch {
    return null;
  }
}

/** Namen ohne führendes Blatt-Präfix und in Kleinbuchstaben, zum Vergleichen. */
export function namenKern(name: string): string {
  return name.replace(/^.*!/, "").toLowerCase();
}

/* ---------- Power Query ---------- */

export interface AbfrageInfo {
  name: string;
  /** "Table", "PivotTable", "ConnectionOnly", … */
  loadedTo: string | null;
  loadedToDataModel: boolean | null;
  rowsLoadedCount: number | null;
  refreshDate: Date | null;
  /** "None", wenn die letzte Aktualisierung fehlerfrei war. */
  error: string | null;
}

/** Power-Query-Abfragen der Arbeitsmappe. `null` vor ExcelApi 1.14. */
export async function readAbfragen(): Promise<AbfrageInfo[] | null> {
  pruefeExcel();
  if (!supportsExcelApi("1.14")) return null;

  try {
    return await Excel.run(async (context) => {
      const queries = context.workbook.queries;
      queries.load("items/name,items/loadedTo,items/loadedToDataModel,items/rowsLoadedCount,items/refreshDate,items/error");
      await context.sync();
      return queries.items.map((query) => {
        const datum = sicher(() => query.refreshDate);
        return {
          name: String(sicher(() => query.name) ?? ""),
          loadedTo: typeof sicher(() => query.loadedTo) === "string" ? String(query.loadedTo) : null,
          loadedToDataModel: sicher(() => query.loadedToDataModel),
          rowsLoadedCount: sicher(() => query.rowsLoadedCount),
          refreshDate: datum ? new Date(datum) : null,
          error: typeof sicher(() => query.error) === "string" ? String(query.error) : null,
        };
      });
    });
  } catch {
    return null;
  }
}

/* ---------- Diagrammdetails ---------- */

export interface ReiheInfo {
  name: string;
  /** Diagrammart der einzelnen Reihe – unterscheidet sich im Kombidiagramm. */
  chartType: string | null;
  /** "Primary" oder "Secondary". `null` vor 1.8. */
  axisGroup: string | null;
  trendlinien: number;
  datenbeschriftung: boolean | null;
}

export interface DiagrammDetails {
  sheetName: string;
  name: string;
  chartType: string | null;
  reihen: ReiheInfo[];
  /** Text des Titels der Größenachse (y-Achse), leer ohne Titel. */
  achsentitel: string;
}

/** Reihen, Trendlinien, Achsen und Beschriftungen jedes Diagramms. `null` vor 1.7. */
export async function readDiagrammDetails(): Promise<DiagrammDetails[] | null> {
  pruefeExcel();
  if (!supportsExcelApi(excelApi.diagrammart)) return null;
  const mitAchsengruppe = supportsExcelApi("1.8");

  try {
    return await Excel.run(async (context) => {
      const sheets = context.workbook.worksheets;
      sheets.load("items/name");
      await context.sync();
      const charts = sheets.items.map((sheet) => {
        sheet.charts.load("items/name,items/chartType");
        return sheet;
      });
      await context.sync();

      const anfragen = charts.flatMap((sheet) =>
        sheet.charts.items.map((chart) => {
          const series = chart.series;
          series.load(
            mitAchsengruppe
              ? "items/name,items/chartType,items/hasDataLabels,items/axisGroup"
              : "items/name,items/chartType,items/hasDataLabels",
          );
          const titel = chart.axes.valueAxis.title;
          titel.load("text,visible");
          return { sheetName: sheet.name, chart, series, titel };
        }),
      );
      await context.sync();

      const trendAnfragen = anfragen.map(({ series }) =>
        series.items.map((reihe) => {
          const trend = reihe.trendlines;
          trend.load("items");
          return trend;
        }),
      );
      await context.sync();

      return anfragen.map(({ sheetName, chart, series, titel }, index) => ({
        sheetName,
        name: chart.name,
        chartType: typeof sicher(() => chart.chartType) === "string" ? String(chart.chartType) : null,
        reihen: series.items.map((reihe, reihenIndex) => ({
          name: String(sicher(() => reihe.name) ?? ""),
          chartType: typeof sicher(() => reihe.chartType) === "string" ? String(reihe.chartType) : null,
          axisGroup: mitAchsengruppe && typeof sicher(() => reihe.axisGroup) === "string" ? String(reihe.axisGroup) : null,
          trendlinien: trendAnfragen[index][reihenIndex]?.items.length ?? 0,
          datenbeschriftung: sicher(() => reihe.hasDataLabels),
        })),
        achsentitel: sicher(() => titel.visible) === true ? String(sicher(() => titel.text) ?? "").trim() : "",
      }));
    });
  } catch {
    return null;
  }
}

/* ---------- Notizen ---------- */

export interface NotizInfo {
  sheetName: string;
  address: string;
  content: string;
}

/** Notizen (die gelben Zettel, früher „Kommentare“). `null` vor ExcelApi 1.18. */
export async function readNotizen(): Promise<NotizInfo[] | null> {
  pruefeExcel();
  if (!supportsExcelApi("1.18")) return null;

  try {
    return await Excel.run(async (context) => {
      const notes = context.workbook.notes;
      notes.load("items/content");
      await context.sync();
      const orte = notes.items.map((note) => {
        const ort = note.getLocation();
        ort.load("address");
        return ort;
      });
      await context.sync();
      return notes.items.map((note, index) => {
        const { sheet, range } = splitAddress(orte[index].address);
        return { sheetName: sheet ?? "", address: range, content: String(sicher(() => note.content) ?? "").trim() };
      });
    });
  } catch {
    return null;
  }
}

/* ---------- Formen ---------- */

export interface FormInfo {
  sheetName: string;
  name: string;
  /** "Image", "GeometricShape", "Group", "Line", … */
  type: string | null;
  altText: string;
}

/** Bilder und Formen auf den Blättern. `null` vor ExcelApi 1.9. */
export async function readFormen(): Promise<FormInfo[] | null> {
  pruefeExcel();
  if (!supportsExcelApi("1.9")) return null;

  try {
    return await Excel.run(async (context) => {
      const sheets = context.workbook.worksheets;
      sheets.load("items/name");
      await context.sync();
      for (const sheet of sheets.items) sheet.shapes.load("items/name,items/type,items/altTextDescription");
      await context.sync();
      return sheets.items.flatMap((sheet) =>
        sheet.shapes.items.map((shape) => ({
          sheetName: sheet.name,
          name: String(sicher(() => shape.name) ?? ""),
          type: typeof sicher(() => shape.type) === "string" ? String(shape.type) : null,
          altText: String(sicher(() => shape.altTextDescription) ?? "").trim(),
        })),
      );
    });
  } catch {
    return null;
  }
}

/* ---------- Rahmenlinien und Hyperlinks je Zelle ---------- */

export interface ZellDetail {
  sheetName: string;
  address: string;
  rahmen: { oben: boolean; unten: boolean; links: boolean; rechts: boolean };
  hyperlink: { ziel: string; text: string } | null;
}

const MAX_ZELLEN = 3000;

/**
 * Rahmenlinien und Hyperlinks aller Zellen des benutzten Bereichs – nur die
 * Zellen, die mindestens eines von beidem haben. `null` vor ExcelApi 1.9.
 */
export async function readZellDetails(): Promise<ZellDetail[] | null> {
  pruefeExcel();
  if (!supportsExcelApi(excelApi.zellformate)) return null;

  try {
    return await Excel.run(async (context) => {
      const sheets = context.workbook.worksheets;
      sheets.load("items/name");
      await context.sync();
      const bereiche = sheets.items.map((sheet) => {
        const used = sheet.getUsedRangeOrNullObject(false);
        used.load("address,cellCount,isNullObject");
        return { sheetName: sheet.name, used };
      });
      await context.sync();

      const anfragen = bereiche
        .filter(({ used }) => !used.isNullObject && used.cellCount <= MAX_ZELLEN)
        .map(({ sheetName, used }) => ({
          sheetName,
          used,
          eigenschaften: used.getCellProperties({
            address: true,
            hyperlink: true,
            format: { borders: { style: true } },
          }),
        }));
      await context.sync();

      const linie = (stil: unknown) => typeof stil === "string" && stil !== "None";
      return anfragen.flatMap(({ sheetName, eigenschaften }) =>
        eigenschaften.value.flat().flatMap((zelle) => {
          const b = zelle.format?.borders;
          const rahmen = {
            oben: linie(b?.top?.style),
            unten: linie(b?.bottom?.style),
            links: linie(b?.left?.style),
            rechts: linie(b?.right?.style),
          };
          const link = zelle.hyperlink;
          const hyperlink =
            link && (link.address || link.documentReference)
              ? { ziel: String(link.address ?? link.documentReference ?? ""), text: String(link.textToDisplay ?? "") }
              : null;
          if (!rahmen.oben && !rahmen.unten && !rahmen.links && !rahmen.rechts && !hyperlink) return [];
          return [{ sheetName, address: splitAddress(String(zelle.address ?? "")).range, rahmen, hyperlink }];
        }),
      );
    });
  } catch {
    return null;
  }
}

/* ---------- Druck: Kopf- und Fußzeilen, Seitenumbrüche ---------- */

export interface DruckDetails {
  sheetName: string;
  kopfzeile: string;
  fusszeile: string;
  /** Manuell gesetzte horizontale Seitenumbrüche. */
  umbrueche: number;
}

/** Kopf- und Fußzeile (für alle Seiten) und manuelle Umbrüche. `null` vor 1.9. */
export async function readDruckDetails(): Promise<DruckDetails[] | null> {
  pruefeExcel();
  if (!supportsExcelApi(excelApi.seitenlayout)) return null;

  try {
    return await Excel.run(async (context) => {
      const sheets = context.workbook.worksheets;
      sheets.load("items/name");
      await context.sync();
      const anfragen = sheets.items.map((sheet) => {
        const alle = sheet.pageLayout.headersFooters.defaultForAllPages;
        alle.load("leftHeader,centerHeader,rightHeader,leftFooter,centerFooter,rightFooter");
        const umbrueche = sheet.horizontalPageBreaks;
        umbrueche.load("items");
        return { sheetName: sheet.name, alle, umbrueche };
      });
      await context.sync();
      return anfragen.map(({ sheetName, alle, umbrueche }) => ({
        sheetName,
        kopfzeile: [alle.leftHeader, alle.centerHeader, alle.rightHeader].filter(Boolean).join(" | "),
        fusszeile: [alle.leftFooter, alle.centerFooter, alle.rightFooter].filter(Boolean).join(" | "),
        umbrueche: umbrueche.items.length,
      }));
    });
  } catch {
    return null;
  }
}

/* ---------- Berechnung und Ansicht ---------- */

/** "Automatic", "AutomaticExceptTables" oder "Manual". */
export async function readBerechnungsmodus(): Promise<string | null> {
  pruefeExcel();
  try {
    return await Excel.run(async (context) => {
      const application = context.workbook.application;
      application.load("calculationMode");
      await context.sync();
      return typeof application.calculationMode === "string" ? application.calculationMode : null;
    });
  } catch {
    return null;
  }
}

/** "normalView", "pageBreakPreview" oder "pageLayoutView". `null` ohne ExcelApiDesktop 1.1. */
export async function readFensteransicht(): Promise<string | null> {
  pruefeExcel();
  if (!isSetSupported("ExcelApiDesktop", "1.1")) return null;
  try {
    return await Excel.run(async (context) => {
      const fenster = context.workbook.application.activeWindow;
      fenster.load("view");
      await context.sync();
      return typeof fenster.view === "string" ? fenster.view : null;
    });
  } catch {
    return null;
  }
}

/** Dateiendung der geöffneten Arbeitsmappe in Kleinbuchstaben, z. B. "xlsm". */
export function dateiendung(): string | null {
  try {
    const adresse = (typeof Office !== "undefined" ? (Office.context?.document?.url ?? "") : "").split(/[?#]/)[0];
    const treffer = /\.([a-z0-9]{3,4})$/i.exec(adresse);
    return treffer ? treffer[1].toLowerCase() : null;
  } catch {
    return null;
  }
}
